import { bridge } from '~/services/bridge'
import { useUserDirHandlesPermission } from '~/hooks/useUserDirHandlesPermission'
import { requestAllPermissions } from '~/store/dirHandleSlice'
import { cm, isChrome } from '~/utils/utils'

const WarningIcon = () => (
  <svg width='22' height='22' viewBox='0 0 22 22' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M11 5.7498C11.1989 5.7498 11.3897 5.82882 11.5304 5.96947C11.671 6.11012 11.75 6.30089 11.75 6.4998V11.7498C11.75 11.9487 11.671 12.1395 11.5304 12.2801C11.3897 12.4208 11.1989 12.4998 11 12.4998C10.8011 12.4998 10.6104 12.4208 10.4697 12.2801C10.3291 12.1395 10.25 11.9487 10.25 11.7498V6.4998C10.25 6.30089 10.3291 6.11012 10.4697 5.96947C10.6104 5.82882 10.8011 5.7498 11 5.7498ZM9.87504 15.1248C9.87504 15.3473 9.94102 15.5648 10.0646 15.7498C10.1882 15.9348 10.364 16.079 10.5695 16.1642C10.7751 16.2493 11.0013 16.2716 11.2195 16.2282C11.4377 16.1848 11.6382 16.0776 11.7955 15.9203C11.9529 15.763 12.06 15.5625 12.1034 15.3443C12.1468 15.1261 12.1245 14.8998 12.0394 14.6943C11.9543 14.4887 11.8101 14.313 11.6251 14.1894C11.44 14.0658 11.2225 13.9998 11 13.9998C10.7017 13.9998 10.4155 14.1183 10.2045 14.3293C9.99356 14.5403 9.87504 14.8264 9.87504 15.1248ZM21.5 10.9998C21.5006 11.1963 21.4622 11.3909 21.387 11.5724C21.3119 11.7539 21.2015 11.9187 21.0622 12.0573L12.0575 21.0629C11.7765 21.3423 11.3963 21.4991 11 21.4991C10.6038 21.4991 10.2236 21.3423 9.94254 21.0629L0.942537 12.0573C0.66316 11.7763 0.506348 11.3961 0.506348 10.9998C0.506348 10.6035 0.66316 10.2233 0.942537 9.9423L9.94722 0.936677C10.2283 0.657301 10.6084 0.500488 11.0047 0.500488C11.401 0.500488 11.7812 0.657301 12.0622 0.936677L21.0669 9.9423C21.2053 10.0813 21.3149 10.2463 21.3892 10.4278C21.4635 10.6093 21.5012 10.8037 21.5 10.9998ZM20 10.9998L11 1.9998L2.00004 10.9998L11 19.9998L20 10.9998Z'
      fill='#EA462D'
    />
  </svg>
)

interface TopAlertBannerProps {
  message?: string
  buttonText?: string
  onButtonClick?: () => void
  buttonProps?: React.ButtonHTMLAttributes<HTMLButtonElement>
  className?: string
}

const TopAlertBanner = ({
  message = 'Curie is unable to access your local repositories. Please re-sync to continue.',
  buttonText = 'Re-sync',
  onButtonClick = () => {
    requestAllPermissions()
  },
  buttonProps = {},
  className = ''
}: TopAlertBannerProps) => {
  const { status: permissionStatus } = useUserDirHandlesPermission()
  let showButton = true

  if (bridge.isInPlugin()) {
    return null
  }

  if (!isChrome()) {
    buttonText = 'Download Chrome'
    message = 'Curie is not fully supported on this browser. Please use Chrome to continue.'
    onButtonClick = () => {
      window.open('https://www.google.com/chrome/', '_blank')
    }
  } else {
    if (permissionStatus === 'approved') {
      return null
    }
    if (permissionStatus === 'rejected') {
      message =
        'Curie is unable to access your local repositories. Please change your site permissions.'
      showButton = false
    }
  }

  return (
    <div className='w-full flex justify-center align-center bg-white'>
      <div
        className={cm(
          `min-w-[800px] max-w-[800px] rounded-lg px-12 py-2 flex justify-between items-center ${className}`,
          'border-1 border-[rgba(234,70,45,0.30)] h-[60px] mt-6 bg-[rgba(234,70,45,0.10)]'
        )}
      >
        <div className='flex items-center gap-4'>
          <div className='w-6 h-6 relative'>
            <div className='w-6 h-6 absolute left-0 top-0' />
            <div className='absolute left-[1.51px] top-[1.50px]'>
              <WarningIcon />
            </div>
          </div>
          <div className='flex justify-center items-center gap-5'>
            <div className='flex items-start gap-[5px]'>
              <div className='text-black text-sm font-normal leading-5'>{message}</div>
            </div>
          </div>
        </div>
        {showButton && (
          <button
            onClick={onButtonClick}
            className={cm(
              'h-[30px] px-4 py-[10px] rounded-lg border border-[#eaeaea] bg-white flex items-center justify-center'
            )}
            {...buttonProps}
          >
            <span className='text-black text-sm font-medium leading-5 tracking-[0.1px]'>
              {buttonText}
            </span>
          </button>
        )}
      </div>
    </div>
  )
}

export default TopAlertBanner
